/** Projections over a session doc and the project session registry. */

import {
  type ActiveRun,
  activeToolWorker,
  decodeLogDoc,
  type Firing,
  type Holding,
  heads,
  type Interaction,
  pendingInteractions,
  projectActiveRun,
  projectActiveRuns,
  projectFirings,
  projectHoldings,
  projectSelection,
  projectStopReports,
  projectTranscript,
  type StampedSelection,
  type StopReport,
  type TranscriptMessage,
} from '../../log'
import {
  readStripActivity,
  stripLiveActivityFromEntry,
  stripReplyFromEntry,
} from '../../log/strip-activity'
import { THREAD_KEY } from '../../log/thread-key'
import type { CommentMap } from '../comments/store'
import { projectString } from '../project/scalars'
import { decorateHoldings, type HeldDocument } from './held-documents'
import { sessionTitleOf } from './title'
import type {
  SessionMetaEntry,
  SessionRunStatus,
  SessionStripActivity,
} from './types'

// Kept here as public model exports for existing callers; the write boundary
// owns their definition so the same byte limits apply before anything reaches
// a session header.
export {
  MAX_STRIP_QUESTION_CHARS,
  MAX_STRIP_REPLY_CHARS,
} from '../../log/strip-activity'

/**
 * Project a session doc's inline log into its transcript. Accepts the raw doc
 * snapshot (`useDocument(sessionId)`); returns `[]` for an absent/empty thread.
 *
 * Sessions are single-doc: the whole conversation lives inline under the session
 * doc's `thread` key, so this inline projection is the full history.
 */
export function projectSessionTranscript(
  sessionDoc: unknown,
): TranscriptMessage[] {
  if (!sessionDoc || typeof sessionDoc !== 'object') return []
  return projectTranscript(decodeLogDoc(sessionDoc as Record<string, unknown>))
}
/**
 * The World agent's stop reports, each with what it decided (RFC 0348 3e),
 * from a RAW session doc. Empty for any other session.
 */
export function projectSessionStopReports(sessionDoc: unknown): StopReport[] {
  if (!sessionDoc || typeof sessionDoc !== 'object') return []
  return projectStopReports(decodeLogDoc(sessionDoc as Record<string, unknown>))
}

/**
 * What this session has in hand (RFC 0297 §4a), from a RAW session doc.
 *
 * The model-level door to `projectHoldings`, beside the transcript and for the
 * same reason: every surface holds a session document rather than a decoded
 * log, and each one decoding for itself is how two of them end up disagreeing
 * about what a session is holding.
 */
export function projectSessionHoldings(sessionDoc: unknown): Holding[] {
  if (!sessionDoc || typeof sessionDoc !== 'object') return []
  return projectHoldings(decodeLogDoc(sessionDoc as Record<string, unknown>))
}
/**
 * What a session is holding, decorated with what the project says (RFC 0297
 * §4c).
 *
 * The one call a surface makes. Both halves are exported separately so the fold
 * can be tested without a project, but a bar that composed them itself would
 * be the second place the rules live.
 */
/**
 * What this session was last pointed at, from a RAW session doc (RFC 0297 §4e).
 *
 * Beside the holdings, because it is only ever read with them: a selection
 * names a passage INSIDE a held document, and on its own it has nowhere to be
 * drawn.
 */
export function projectSessionSelection(
  sessionDoc: unknown,
): StampedSelection | null {
  if (!sessionDoc || typeof sessionDoc !== 'object') return null
  return projectSelection(decodeLogDoc(sessionDoc as Record<string, unknown>))
}
export function projectSessionHeldDocuments(
  sessionDoc: unknown,
  projectDoc: unknown,
  sessionId?: string,
  listed?: CommentMap,
): HeldDocument[] {
  return decorateHoldings(
    projectSessionHoldings(sessionDoc),
    projectDoc,
    sessionId,
    listed,
  )
}
/**
 * The session's active run, from a RAW session doc (RFC 0269).
 *
 * The raw-doc wrapper every surface actually calls, matching
 * projectSessionTranscript: a client holds an Automerge doc, not a decoded
 * LogDoc, and should not have to know about the codec to ask "is the agent
 * busy".
 */
export function projectSessionActiveRun(sessionDoc: unknown): ActiveRun | null {
  if (!sessionDoc || typeof sessionDoc !== 'object') return null
  return projectActiveRun(decodeLogDoc(sessionDoc as Record<string, unknown>))
}
/** RFC 0301 §3f: what this session's pairings have done, from a RAW doc. */
export function projectSessionFirings(sessionDoc: unknown): Firing[] {
  if (!sessionDoc || typeof sessionDoc !== 'object') return []
  return projectFirings(decodeLogDoc(sessionDoc as Record<string, unknown>))
}
/** Every active run on a RAW session doc. A session can hold more than one. */
export function projectSessionActiveRuns(sessionDoc: unknown): ActiveRun[] {
  if (!sessionDoc || typeof sessionDoc !== 'object') return []
  return projectActiveRuns(decodeLogDoc(sessionDoc as Record<string, unknown>))
}
/**
 * The questions this session is blocked on, from a RAW session doc.
 *
 * Oldest first, so a surface that shows exactly one prompt shows the one that
 * has been holding up the turn longest.
 */
export function projectSessionPendingInteractions(
  sessionDoc: unknown,
): Interaction[] {
  if (!sessionDoc || typeof sessionDoc !== 'object') return []
  return pendingInteractions(
    decodeLogDoc(sessionDoc as Record<string, unknown>),
  )
}
/**
 * What a session is doing, for a surface watching several of them (RFC 0286).
 *
 * `complete` is deliberately absent: it is the one part of a session's status
 * that is STORED, on the project registry entry, and a caller reading that
 * registry already has it without opening this document. Returning it here would
 * mean this function needed the project too, and every caller would have to
 * decide which of two answers won.
 */
/**
 * Project a session's run status from its RAW doc.
 *
 * ONE definition, so the list, the tools and the CLI cannot drift into three
 * slightly different ideas of "working". Ordered by what a person needs to know
 * first: a question outranks a run, because the run is not going anywhere until
 * the question is answered.
 */
export function projectSessionStatus(sessionDoc: unknown): SessionRunStatus {
  if (projectSessionPendingInteractions(sessionDoc).length > 0) return 'asking'
  const runs = projectSessionActiveRuns(sessionDoc)
  if (runs.length === 0) return 'idle'
  // `claimed` is the difference between a slow agent and no agent at all, and
  // the fix is different in each case, so the two never collapse into one word.
  return runs.some((r) => r.claimed) ? 'running' : 'queued'
}

/**
 * Project only what the compact composer needs from its session (RFC 0304 3h).
 *
 * Reads the bounded header the log writes on the session document, and at most
 * the one message it points at, so the strip follows the live session without
 * decoding its whole transcript.
 */
export function projectSessionStripActivity(
  sessionDoc: unknown,
  status: SessionRunStatus = projectSessionStatus(sessionDoc),
): SessionStripActivity | null {
  if (!sessionDoc || typeof sessionDoc !== 'object') return null
  const raw = sessionDoc as {
    stripActivity?: unknown
    stripReplyMessageId?: unknown
    [THREAD_KEY]?: { entries?: Record<string, unknown> }
  }
  if (status === 'asking') {
    const activity = readStripActivity(raw.stripActivity)
    return activity?.kind === 'question' ? activity : null
  }
  const messageId = raw.stripReplyMessageId
  if (messageId == null) return null
  if (status === 'running')
    return stripLiveActivityFromEntry(
      raw[THREAD_KEY]?.entries?.[String(messageId)],
    )
  if (status !== 'idle') return null
  return stripReplyFromEntry(raw[THREAD_KEY]?.entries?.[String(messageId)])
}
/**
 * How long a non-idle status is believed (RFC 0284 S8a).
 *
 * The presence window, for the same reason and by the same argument: a writer
 * that dies stops refreshing, and its claim ages out without anything having to
 * notice the death. 30s matches `DEFAULT_PRESENCE_TTL_MS`, so a runtime already
 * heartbeating on the ~10s device cadence keeps a run fresh with margin.
 */
export const STATUS_FRESH_MS = 30_000
/**
 * What the REGISTRY says a session is doing, or `undefined` for "cannot tell"
 * (RFC 0284 S8a).
 *
 * `undefined` rather than `'idle'`, always. A session nobody has reported on is
 * not a quiet one, and reporting it as quiet is how a stuck session gets
 * skipped. A caller that needs certainty hydrates the row; a caller that cannot
 * afford to, like an installed sidebar, draws nothing for it.
 *
 * IDLE NEVER GOES STALE. A session that stopped is still stopped, however long
 * ago the runtime said so, so freshness gates only the claims that assert
 * something is HAPPENING. Ageing out idle would make every quiet session
 * unknown after thirty seconds, which is every session most of the time.
 *
 * NOR DOES ASKING (RFC 0336 S10). A question waits on a person, not on a
 * runtime: it is still pending however long ago it was asked, and a runtime
 * that died while it waited does not answer it. The claim ends when it is
 * answered or dismissed (`answerInteraction` writes the next claim) or when a
 * runtime writes a newer one. Every surface reads this one rule.
 */
export function registryStatus(
  entry: { status?: unknown; statusAt?: unknown } | undefined,
  now: number,
  freshMs = STATUS_FRESH_MS,
): SessionRunStatus | undefined {
  const status = projectString(entry?.status)
  if (
    status !== 'idle' &&
    status !== 'queued' &&
    status !== 'running' &&
    status !== 'asking'
  )
    return undefined
  if (status === 'idle') return 'idle'
  const at = entry?.statusAt
  if (status === 'asking') return typeof at === 'number' ? 'asking' : undefined
  // A CLAIM WITH NO TIMESTAMP is unbelievable rather than believed: it is what
  // a writer that half-implements this field produces, and trusting it would
  // reintroduce the "working forever" failure the timestamp exists to prevent.
  if (typeof at !== 'number') return undefined
  return now - at <= freshMs ? status : undefined
}
/**
 * Every status the REGISTRY can answer, for a whole project (RFC 0284 S8a).
 *
 * Zero hydration: one document read, however many sessions. That is the point
 * of the field, and it is what lets a list of two hundred draw statuses when
 * opening two hundred session documents to compute them could not.
 *
 * ONLY THE ONES IT CAN ANSWER. A session with no fresh claim is simply absent
 * from the map, so a caller can see exactly which rows still need the
 * expensive path and hydrate those alone. An entry of `undefined` would make
 * "the registry says idle" and "the registry cannot tell" the same key.
 */
export function projectSessionStatuses(
  projectDoc: unknown,
  now: number,
  freshMs = STATUS_FRESH_MS,
): Record<string, SessionRunStatus> {
  const out: Record<string, SessionRunStatus> = {}
  if (!projectDoc || typeof projectDoc !== 'object') return out
  const meta = (projectDoc as { sessionMeta?: Record<string, unknown> })
    .sessionMeta
  if (!meta || typeof meta !== 'object') return out
  for (const [id, entry] of Object.entries(meta)) {
    const status = registryStatus(
      entry as { status?: unknown; statusAt?: unknown },
      now,
      freshMs,
    )
    if (status) out[id] = status
  }
  return out
}
/** One session as a list sees it, from the project registry. */
export interface SessionRef {
  id: string
  title: string
  updatedAt: number
  /** When it was filed as done (RFC 0286), by a person or by an agent they
   *  asked. Absent means not done. The only part of a session's status that
   *  comes back without hydrating its doc. */
  completedAt?: number
  /** The session that opened this one, when an agent did. Absent means a person
   *  started it, which is what a list shows at the top level. */
  parentSessionId?: string
}
/**
 * Project a project doc's session registry (`sessionIds` + `sessionMeta`),
 * most-recently-updated first — the same zero-hydration read the session list
 * relies on. What `mod.sessions.list()` resolves to for an app that reads the
 * Sessions interface (RFC 0267).
 */
export function projectSessions(projectDoc: unknown): SessionRef[] {
  if (!projectDoc || typeof projectDoc !== 'object') return []
  const ws = projectDoc as {
    sessionIds?: unknown
    sessionMeta?: Record<
      string,
      {
        title?: unknown
        titleSetBy?: unknown
        agentTitle?: unknown
        updatedAt?: unknown
        completedAt?: unknown
        parentSessionId?: unknown
      }
    >
  }
  const ids = Array.isArray(ws.sessionIds) ? ws.sessionIds : []
  const meta = ws.sessionMeta ?? {}
  const out: SessionRef[] = []
  for (const raw of ids) {
    const id = projectString(raw)
    if (!id) continue
    const m = meta[id] ?? {}
    out.push({
      id,
      // A person's title wins, then the agent's name, then the stub (RFC 0311).
      title:
        sessionTitleOf(m as Parameters<typeof sessionTitleOf>[0]) ||
        id.slice(0, 8),
      updatedAt: typeof m.updatedAt === 'number' ? m.updatedAt : 0,
      ...(typeof m.completedAt === 'number'
        ? { completedAt: m.completedAt }
        : {}),
      ...(projectString(m.parentSessionId)
        ? { parentSessionId: projectString(m.parentSessionId) }
        : {}),
    })
  }
  return out.sort((a, b) => b.updatedAt - a.updatedAt)
}
/**
 * The device this session's runs are addressed to (RFC 0261 slice 3), or null if
 * none is selected — an open claim.
 *
 * This is whiffletree's `selected_tool_worker`: the latest `worker_migration`'s
 * `workerId`. A composer placement picker reads it to show the current choice,
 * and writes a new one with `selectSessionDevice`. Accepts the raw doc snapshot
 * (as `useDocument` hands it over); null for an absent/empty thread.
 */
export function projectSelectedDevice(sessionDoc: unknown): string | null {
  if (!sessionDoc || typeof sessionDoc !== 'object') return null
  const log = decodeLogDoc(sessionDoc as Record<string, unknown>)
  const hs = heads(log)
  // A linear thread has one head, which captures the current selection.
  return hs.length > 0 ? activeToolWorker(log, hs[0]) : null
}
/**
 * How long after its last use a session still counts as being in a document.
 *
 * A write puts a document in hand, and only a put-down takes it out, so without
 * this a header would show every session that ever wrote the document. Three
 * days keeps yesterday's work visible and lets last month's go.
 */
export const HOLDER_RECENT_MS = 3 * 24 * 60 * 60 * 1000

/** Working, waiting, or used within `HOLDER_RECENT_MS`. */
function isAround(entry: SessionMetaEntry | undefined, now: number): boolean {
  const status = registryStatus(entry, now)
  if (status && status !== 'idle') return true
  const at = Number(entry?.updatedAt ?? 0)
  return now - at < HOLDER_RECENT_MS
}

/**
 * How long after a session is filed as done its own turn may still write.
 *
 * An agent files itself done at the end of a turn, and that turn may still be
 * sending its last message a moment later. That is not new work.
 */
export const DONE_GRACE_MS = 120_000

/**
 * Whether a session counts as DONE: filed (`completedAt`), and not started
 * again since. A READ rule, not a write: filing writes only `completedAt` and
 * nothing clears it, so a session that runs again would sit under Done.
 *
 * Out of Done when it is running, queued or asking NOW, or when a message was
 * SENT to it (`sentAt`) more than `DONE_GRACE_MS` after it was filed. Only a
 * send counts. `statusAt` is not work: every daemon start stamps the sessions
 * it hosts idle, which un-filed every done session at once. `updatedAt` is not
 * either: a rename or the namer moves it.
 */
export function isSessionDone(
  entry:
    | {
        completedAt?: unknown
        sentAt?: unknown
        status?: unknown
      }
    | null
    | undefined,
): boolean {
  const filed = entry?.completedAt
  if (typeof filed !== 'number') return false
  const status = String(entry?.status ?? '')
  if (status === 'running' || status === 'queued' || status === 'asking') {
    return false
  }
  const sent = entry?.sentAt
  return !(typeof sent === 'number' && sent > filed + DONE_GRACE_MS)
}

/**
 * The sessions holding a document, most recently active first (RFC 0311 3d).
 *
 * REGISTRY ONLY. It reads `sessionMeta`, which a surface already has loaded,
 * and never opens a session document: a header that hydrated every session in
 * the project to draw three marks would cost more than the document it sits on.
 * A session filed as done is left out; it is not in the document any more.
 */
export function projectDocumentHolders(
  projectDoc: unknown,
  documentId: string,
  now: number = Date.now(),
): string[] {
  const meta = (
    projectDoc as { sessionMeta?: Record<string, SessionMetaEntry> }
  )?.sessionMeta
  if (!meta) return []
  return Object.entries(meta)
    .filter(
      ([, e]) =>
        !isSessionDone(e) &&
        isAround(e, now) &&
        Array.isArray(e?.holding) &&
        e.holding.some((id) => String(id) === documentId),
    )
    .sort(([, a], [, b]) => (b?.updatedAt ?? 0) - (a?.updatedAt ?? 0))
    .map(([id]) => id)
}

/** The projects a session works in besides its home, in the order added
 *  (RFC 0362 3c). Strings off the log may be ImmutableStrings. */
export function sessionAddedProjects(sessionDoc: unknown): string[] {
  const list = (sessionDoc as { addedProjectIds?: unknown } | null | undefined)
    ?.addedProjectIds
  return Array.isArray(list)
    ? [...new Set(list.map((p) => String(p)).filter(Boolean))]
    : []
}
